import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Briefcase, Building2, ChevronLeft, ChevronRight, Globe2, MapPin } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { WrappedStats } from '../components/wrapped/types';
import SeoMeta from '../components/SeoMeta';

interface Nomination {
  id: string;
  category_name: string;
  category_type: 'project' | 'personal' | 'benchmark';
  status: 'approved' | 'pending' | 'rejected';
  country?: string;
  region?: string;
  administration_type?: string;
  candidate_name?: string;
  candidate_position?: string;
  candidate_institution?: string;
  candidate_reason?: string;
  project_title?: string;
  institution?: string;
  project_summary?: string;
  target_audience?: string;
  innovation_description?: string;
  collaboration_description?: string;
  replicability_description?: string;
  impact_description?: string;
  cost_benefit_description?: string;
  sustainability_description?: string;
  benchmark_edicion_categoria?: string;
  benchmark_descripcion_breve?: string;
  benchmark_impacto_sostenido?: string;
  benchmark_elementos_replicables?: string;
  benchmark_administraciones_replicadoras?: string;
  benchmark_mejoras_aprendizajes?: string;
}

const STATUS_LABEL: Record<string, string> = {
  approved: 'Aprobada',
  pending: 'En valoración',
  rejected: 'No admitida',
};

const PROJECT_FIELDS: [keyof Nomination, string][] = [
  ['institution', 'Institución'],
  ['project_summary', 'Resumen de la iniciativa'],
  ['target_audience', 'Público objetivo'],
  ['innovation_description', 'Innovación aportada'],
  ['collaboration_description', 'Colaboración'],
  ['replicability_description', 'Replicabilidad y benchmarking'],
  ['impact_description', 'Impacto y evaluación'],
  ['cost_benefit_description', 'Equilibrio coste/beneficio'],
  ['sustainability_description', 'Sostenibilidad'],
];
const PERSONAL_FIELDS: [keyof Nomination, string][] = [['candidate_reason', 'Justificación de la candidatura']];
const BENCHMARK_FIELDS: [keyof Nomination, string][] = [
  ['benchmark_edicion_categoria', 'Edición/categoría en la que fue premiado'],
  ['benchmark_descripcion_breve', 'Descripción breve del proyecto'],
  ['benchmark_impacto_sostenido', 'Impacto sostenido acreditado'],
  ['benchmark_elementos_replicables', 'Elementos que lo hacen replicable'],
  ['benchmark_administraciones_replicadoras', 'Administraciones que lo han replicado'],
  ['benchmark_mejoras_aprendizajes', 'Mejoras o aprendizajes que ha inspirado'],
];

const TYPE_DOT: Record<string, string> = {
  project: '#ffd702',
  personal: '#7fd1ff',
  benchmark: '#b98bff',
};
const TYPE_ORDER: Record<string, number> = { project: 0, personal: 1, benchmark: 2 };
const isLatam = (name: string) => name.toUpperCase().includes('LATAM');

const shortAdmin = (name?: string) =>
  name ? name.replace(/^Administraci[oó]n P[uú]blica\s*/i, '').replace(/^./, (m) => m.toUpperCase()) : null;

const NominationCard: React.FC<{ item: Nomination; index: number; total: number }> = ({ item, index, total }) => {
  const fields =
    item.category_type === 'project' ? PROJECT_FIELDS : item.category_type === 'benchmark' ? BENCHMARK_FIELDS : PERSONAL_FIELDS;
  const title =
    item.category_type === 'personal'
      ? item.candidate_name || `Candidatura personal — ${item.category_name}`
      : item.project_title || item.benchmark_edicion_categoria || 'Candidatura sin título';
  const personalSubtitle =
    item.category_type === 'personal'
      ? [item.candidate_position, item.candidate_institution].filter(Boolean).join(' · ')
      : '';
  const metaBits = [
    item.country && { Icon: Globe2, label: item.country },
    item.region && { Icon: MapPin, label: item.region },
    shortAdmin(item.administration_type) && { Icon: Building2, label: shortAdmin(item.administration_type) as string },
  ].filter(Boolean) as { Icon: typeof Globe2; label: string }[];

  return (
    <article className="informe-ficha">
      <div className="informe-ficha-head">
        <span className="informe-ficha-num">CANDIDATURA {index + 1} DE {total}</span>
        <span className={`informe-status-pill st-${item.status}`}>{STATUS_LABEL[item.status] || item.status}</span>
      </div>
      <h4 className="informe-ficha-title">{title}</h4>
      {personalSubtitle && (
        <p className="informe-ficha-subtitle">
          <Briefcase size={14} /> {personalSubtitle}
        </p>
      )}
      {metaBits.length > 0 && (
        <div className="informe-ficha-meta-row">
          {metaBits.map((m, i) => (
            <span className="informe-ficha-meta-chip" key={i}>
              <m.Icon size={13} /> {m.label}
            </span>
          ))}
        </div>
      )}
      <div className="informe-ficha-body">
        {fields.map(([key, label]) => {
          const value = item[key] as string | undefined;
          if (!value || !value.trim()) return null;
          return (
            <div className="informe-ficha-field" key={String(key)}>
              <span className="informe-flabel">{label}</span>
              {value.split(/\n+/).filter(Boolean).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          );
        })}
      </div>
    </article>
  );
};

const InformeCandidaturas: React.FC = () => {
  const [stats, setStats] = useState<WrappedStats | null>(null);
  const [nominations, setNominations] = useState<Nomination[] | null>(null);
  const [error, setError] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [fichaIndex, setFichaIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [statsRes, nomsRes] = await Promise.all([
        supabase.schema('premios').rpc('get_public_stats'),
        supabase.schema('premios').rpc('get_public_nominations'),
      ]);
      if (cancelled) return;
      if (statsRes.error || nomsRes.error || !statsRes.data || !nomsRes.data) {
        console.error('Error cargando informe:', statsRes.error || nomsRes.error);
        setError(true);
        return;
      }
      setStats(statsRes.data as WrappedStats);
      setNominations(nomsRes.data as Nomination[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const byCategory: Record<string, Nomination[]> = {};
  (nominations || [])
    .filter((n) => n.status === 'approved')
    .forEach((n) => {
      byCategory[n.category_name] = byCategory[n.category_name] || [];
      byCategory[n.category_name].push(n);
    });

  const activeItems = activeCategory ? byCategory[activeCategory] || [] : [];

  // Navegación por teclado dentro del visor de una categoría.
  useEffect(() => {
    if (!activeCategory) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setFichaIndex((i) => Math.min(activeItems.length - 1, i + 1));
      else if (e.key === 'ArrowLeft') setFichaIndex((i) => Math.max(0, i - 1));
      else if (e.key === 'Escape') setActiveCategory(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, activeItems.length]);

  const openCategory = (name: string) => {
    setActiveCategory(name);
    setFichaIndex(0);
  };

  if (error) {
    return (
      <div className="informe-page">
        <div className="informe-page-content" style={{ textAlign: 'center', paddingTop: 80 }}>
          <p>No se ha podido cargar el informe. Inténtalo de nuevo en unos instantes.</p>
          <Link to="/candidaturas" className="wrapped-btn primary" style={{ marginTop: 16 }}>
            Volver
          </Link>
        </div>
      </div>
    );
  }

  if (!stats || !nominations) {
    return (
      <div className="informe-page">
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
          <div className="wrapped-spinner" />
        </div>
      </div>
    );
  }

  /* -------------------------------------------------- Visor de una categoría */
  if (activeCategory) {
    const item = activeItems[fichaIndex];
    const isFirst = fichaIndex === 0;
    const isLast = fichaIndex === activeItems.length - 1;

    return (
      <div className="informe-page">
        <SeoMeta
          title={`${activeCategory} · Informe de candidaturas · Premios NovaGob Innovación Pública`}
          description={`Candidaturas presentadas en la categoría "${activeCategory}" de los Premios NovaGob Excelencia: iniciativas de innovación pública y transformación digital en la Administración Pública.`}
          path="/informe-candidaturas"
        />
        <header className="informe-page-header">
          <button type="button" className="informe-page-back" onClick={() => setActiveCategory(null)}>
            <ArrowLeft size={18} /> Categorías
          </button>
          <span className="informe-page-title">
            {activeCategory} · {fichaIndex + 1} / {activeItems.length}
          </span>
          <Link to="/" className="informe-page-home">
            Volver al inicio
          </Link>
        </header>

        <div className="informe-viewer">
          <button
            type="button"
            className="informe-viewer-arrow left"
            onClick={() => setFichaIndex((i) => Math.max(0, i - 1))}
            disabled={isFirst}
            aria-label="Candidatura anterior"
          >
            <ChevronLeft size={26} />
          </button>

          <div className="informe-viewer-stage">
            {item && <NominationCard item={item} index={fichaIndex} total={activeItems.length} />}
          </div>

          <button
            type="button"
            className="informe-viewer-arrow right"
            onClick={() => setFichaIndex((i) => Math.min(activeItems.length - 1, i + 1))}
            disabled={isLast}
            aria-label="Siguiente candidatura"
          >
            <ChevronRight size={26} />
          </button>
        </div>

        <div className="informe-viewer-mobilenav">
          <button type="button" onClick={() => setFichaIndex((i) => Math.max(0, i - 1))} disabled={isFirst}>
            <ChevronLeft size={18} /> Anterior
          </button>
          <button type="button" onClick={() => setFichaIndex((i) => Math.min(activeItems.length - 1, i + 1))} disabled={isLast}>
            Siguiente <ChevronRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  /* -------------------------------------------------------------- Inicio */
  const orderedCategories = [...stats.categories].sort((a, b) => {
    const typeA = byCategory[a.name]?.[0]?.category_type || 'project';
    const typeB = byCategory[b.name]?.[0]?.category_type || 'project';
    if (TYPE_ORDER[typeA] !== TYPE_ORDER[typeB]) return TYPE_ORDER[typeA] - TYPE_ORDER[typeB];
    const latamA = isLatam(a.name);
    const latamB = isLatam(b.name);
    if (latamA !== latamB) return latamA ? 1 : -1;
    return b.count - a.count;
  });
  const catGridRows = Math.ceil(orderedCategories.length / 2);

  return (
    <div className="informe-page">
      <SeoMeta
        title="Informe de Candidaturas · Premios NovaGob a la Innovación Pública 2026"
        description="Consulta el informe completo, categoría a categoría, de las candidaturas presentadas a los Premios NovaGob Excelencia: proyectos e iniciativas de innovación pública y transformación digital en la Administración Pública de España y Latinoamérica."
        path="/informe-candidaturas"
      />
      <header className="informe-page-header">
        <Link to="/candidaturas" className="informe-page-back">
          <ArrowLeft size={18} /> Volver
        </Link>
        <span className="informe-page-title">Informe de candidaturas · Edición 2026</span>
        <Link to="/" className="informe-page-home">
          Volver al inicio
        </Link>
      </header>

      <div className="informe-page-content">
        <section className="bases-modal-section">
          <h3>¿Qué contiene este informe?</h3>
          <p>
            Recopila el contenido de las candidaturas presentadas a los Premios NovaGob Excelencia 2026.
          </p>
          <p>
            Con el objetivo de proteger la privacidad de las personas y entidades candidatas, se ha eliminado
            el correo electrónico de quien presenta la candidatura y cualquier enlace a CV o perfiles
            personales (LinkedIn u otras redes). En las candidaturas a título personal se muestra el nombre y
            el cargo de la persona candidata; se conserva el resto del contenido redactado en el formulario:
            título, resumen, justificación, descripciones de impacto, innovación, colaboración, replicabilidad
            y sostenibilidad, según corresponda al tipo de categoría.
          </p>
        </section>

        <section className="bases-modal-section">
          <h3>Resumen de la edición</h3>
          <div className="informe-stat-grid">
            <div className="informe-stat-box">
              <div className="informe-stat-num">{stats.totals.received}</div>
              <div className="informe-stat-lbl">Recibidas</div>
            </div>
            <div className="informe-stat-box">
              <div className="informe-stat-num">{stats.totals.approved}</div>
              <div className="informe-stat-lbl">Aprobadas</div>
            </div>
            <div className="informe-stat-box">
              <div className="informe-stat-num">{stats.totals.rejected}</div>
              <div className="informe-stat-lbl">No admitidas</div>
            </div>
            <div className="informe-stat-box">
              <div className="informe-stat-num">{stats.countries.length}</div>
              <div className="informe-stat-lbl">Países</div>
            </div>
            <div className="informe-stat-box">
              <div className="informe-stat-num">{stats.institutions.count}</div>
              <div className="informe-stat-lbl">Instituciones</div>
            </div>
          </div>
        </section>

        <section className="bases-modal-section">
          <h3>¿Qué candidaturas quieres revisar?</h3>
          <p style={{ marginBottom: 18 }}>
            Elige una categoría para ver, una a una, solo las candidaturas de esa categoría.
          </p>
          <div
            className="informe-catgrid"
            style={{ ['--cat-rows' as any]: catGridRows }}
          >
            {orderedCategories.map((c) => (
              <button
                key={c.name}
                type="button"
                className="informe-catcard"
                onClick={() => openCategory(c.name)}
                disabled={!byCategory[c.name] || byCategory[c.name].length === 0}
              >
                <span
                  className="informe-catcard-dot"
                  style={{ background: TYPE_DOT[byCategory[c.name]?.[0]?.category_type || 'project'] }}
                />
                <span className="informe-catcard-name">{c.name}</span>
                <span className="informe-catcard-count">{(byCategory[c.name] || []).length}</span>
                <ChevronRight size={16} className="informe-catcard-arrow" />
              </button>
            ))}
          </div>
        </section>

        <div className="bases-modal-footer">
          <p className="bases-modal-footer-credits">
            Documento de uso interno · No debe utilizarse para identificar a las entidades candidatas ni a las
            personas que presentan las candidaturas de proyecto.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InformeCandidaturas;
